/**
 * Budgets slice (PX-W6.4) — the active revision, the revision history and
 * the budget-vs-actual roll-up of the opened project.
 *
 * Absence is a fact (I-7): a 404 on the active GET lands as
 * `activeMissing: true` with `active: null` — the page renders the explicit
 * "no budget agreed" state, never an error panel, never a zero. A revision
 * PUT invalidates all three caches and refetches them (non-optimistic); the
 * totals the view renders are ALWAYS the server's (`actualCostMinorTotal`,
 * `remainingMinor`, `consumedPct`) — nothing is re-derived or re-summed
 * client-side, and a negative `remainingMinor` (an overrun) is stored and
 * rendered as-is, never clamped.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { budgetServiceHttp } from '../services/budgetService.http'
import type { BudgetVsActualView, ProjectBudget, PutBudgetInput } from '../types'

/** A failed thunk's payload — key, message, and the HTTP status (the 404 branch reads it). */
export interface BudgetsError {
  key?: string
  status?: number
  message: string
}

export interface BudgetsState {
  active: ProjectBudget | null
  /** True when the server answered 404 for the active budget — "no budget agreed". */
  activeMissing: boolean
  history: ProjectBudget[]
  view: BudgetVsActualView | null
  caches: { active: FetchCache; history: FetchCache; view: FetchCache }
  mutating: boolean
  error: BudgetsError | null
}

const initialState: BudgetsState = {
  active: null,
  activeMissing: false,
  history: [],
  view: null,
  caches: {
    active: { ...initialFetchCache },
    history: { ...initialFetchCache },
    view: { ...initialFetchCache }
  },
  mutating: false,
  error: null
}

const toBudgetsError = (error: unknown): BudgetsError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { key: apiError.backendKey, status: apiError.status, message: apiError.message }
}

/** Whether a rejection payload is the server's "no active budget" 404. */
const isMissingBudget = (error: BudgetsError | undefined): boolean => error?.status === 404

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Load the active revision — a 404 is the no-budget fact, not a failure. */
export const fetchActiveBudget = createAsyncThunk<
  ProjectBudget,
  string,
  { rejectValue: BudgetsError }
>('budgets/fetchActiveBudget', async (projectId, { rejectWithValue }) => {
  try {
    return await budgetServiceHttp.active(projectId)
  } catch (error) {
    return rejectWithValue(toBudgetsError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { budgets } = getState() as { budgets: BudgetsState }
    return shouldFetch(budgets.caches.active, projectId)
  }
})

/** Load the revision history (empty list = never budgeted). */
export const fetchBudgetHistory = createAsyncThunk<
  ProjectBudget[],
  string,
  { rejectValue: BudgetsError }
>('budgets/fetchBudgetHistory', async (projectId, { rejectWithValue }) => {
  try {
    return await budgetServiceHttp.history(projectId)
  } catch (error) {
    return rejectWithValue(toBudgetsError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { budgets } = getState() as { budgets: BudgetsState }
    return shouldFetch(budgets.caches.history, projectId)
  }
})

/** Load the roll-up view. */
export const fetchBudgetVsActual = createAsyncThunk<
  BudgetVsActualView,
  string,
  { rejectValue: BudgetsError }
>('budgets/fetchBudgetVsActual', async (projectId, { rejectWithValue }) => {
  try {
    return await budgetServiceHttp.rollup(projectId)
  } catch (error) {
    return rejectWithValue(toBudgetsError(error))
  }
}, {
  condition: (projectId, { getState }) => {
    const { budgets } = getState() as { budgets: BudgetsState }
    return shouldFetch(budgets.caches.view, projectId)
  }
})

/** Agree a new revision, then refetch all three reads (the caches are re-armed first). */
export const putBudgetRevision = createAsyncThunk<
  ProjectBudget,
  { projectId: string; input: PutBudgetInput },
  { rejectValue: BudgetsError }
>('budgets/putBudgetRevision', async ({ projectId, input }, { dispatch, rejectWithValue }) => {
  try {
    const revision = await budgetServiceHttp.putRevision(projectId, input)
    dispatch(budgetsMutated())
    await Promise.all([
      dispatch(fetchActiveBudget(projectId)),
      dispatch(fetchBudgetHistory(projectId)),
      dispatch(fetchBudgetVsActual(projectId))
    ])
    return revision
  } catch (error) {
    return rejectWithValue(toBudgetsError(error))
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const budgetsSlice = createSlice({
  name: 'budgets',
  initialState,
  reducers: {
    /** A revision landed — re-arm all three caches (the thunk refetches after). */
    budgetsMutated(state) {
      invalidate(state.caches.active)
      invalidate(state.caches.history)
      invalidate(state.caches.view)
    },
    /** Dismiss a surfaced error and re-arm the reads (the Retry path). */
    budgetsErrorDismissed(state) {
      state.error = null
      invalidate(state.caches.active)
      invalidate(state.caches.history)
      invalidate(state.caches.view)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveBudget.pending, (state, action) => {
        if (state.caches.active.loadedKey !== action.meta.arg) {
          state.active = null
          state.activeMissing = false
        }
        fetchStarted(state.caches.active)
        state.error = null
      })
      .addCase(fetchActiveBudget.fulfilled, (state, action) => {
        state.active = action.payload
        state.activeMissing = false
        fetchSucceeded(state.caches.active, action.meta.arg)
      })
      .addCase(fetchActiveBudget.rejected, (state, action) => {
        fetchFailed(state.caches.active)
        if (isMissingBudget(action.payload)) {
          // Absence is a fact, not an error (I-7).
          state.active = null
          state.activeMissing = true
          state.error = null
        } else {
          state.error = action.payload ?? null
        }
      })
      .addCase(fetchBudgetHistory.pending, (state, action) => {
        if (state.caches.history.loadedKey !== action.meta.arg) state.history = []
        fetchStarted(state.caches.history)
        state.error = null
      })
      .addCase(fetchBudgetHistory.fulfilled, (state, action) => {
        state.history = action.payload ?? []
        fetchSucceeded(state.caches.history, action.meta.arg)
      })
      .addCase(fetchBudgetHistory.rejected, (state, action) => {
        fetchFailed(state.caches.history)
        state.error = action.payload ?? null
      })
      .addCase(fetchBudgetVsActual.pending, (state, action) => {
        if (state.caches.view.loadedKey !== action.meta.arg) state.view = null
        fetchStarted(state.caches.view)
        state.error = null
      })
      .addCase(fetchBudgetVsActual.fulfilled, (state, action) => {
        state.view = action.payload
        fetchSucceeded(state.caches.view, action.meta.arg)
      })
      .addCase(fetchBudgetVsActual.rejected, (state, action) => {
        fetchFailed(state.caches.view)
        state.error = action.payload ?? null
      })
      .addCase(putBudgetRevision.pending, (state) => {
        state.mutating = true
        state.error = null
      })
      .addCase(putBudgetRevision.fulfilled, (state, action) => {
        state.mutating = false
        // The refetches above land the fresh rows; the returned revision is
        // the truth of THIS write.
        state.active = action.payload
        state.activeMissing = false
      })
      .addCase(putBudgetRevision.rejected, (state, action) => {
        state.mutating = false
        state.error = action.payload ?? null
      })
  }
})

export const { budgetsMutated, budgetsErrorDismissed } = budgetsSlice.actions
